import * as SwitchPrimitives from '@radix-ui/react-switch';
import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { ToggleVariant } from './toggle.type';
import { InfoTooltip } from '@components/atoms';
import type { LabelProps } from '@components/atoms/typography';
import { Label } from '@components/atoms/typography';
import { cn } from '@utils';

export interface ToggleProps extends PropsWithChildren<ComponentProps<typeof SwitchPrimitives.Root>> {
	label?: string;
	description?: string;
	info?: React.ReactNode | string;
	labelProps?: LabelProps;
	variant?: ToggleVariant;
	/** Extra content rendered inline after the label, e.g. a tooltip-wrapped icon. */
	titleAdornment?: React.ReactNode;
}

const Toggle = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, ToggleProps>(
	(
		{
			label = '',
			description,
			info,
			className,
			children,
			id,
			labelProps = {},
			checked,
			variant = ToggleVariant.PRIMARY,
			titleAdornment,
			...props
		},
		ref
	) => {
		const isRtl = props.dir === 'rtl';
		const labelId = id || `toggle-${Math.random().toString(36).substring(7)}`;

		// Determine layout based on variant
		const isCompactToggle = variant === ToggleVariant.COMPACT;

		const labelElement = (
			<Label
				label={label}
				description={isCompactToggle ? undefined : description}
				htmlFor={labelId}
				{...labelProps}
			/>
		);

		return (
			<div dir={props.dir} className={cn('flex-start flex flex-col justify-between gap-x-2', className)}>
				<div className={cn('flex items-center gap-x-2', isCompactToggle ? 'flex-row' : 'flex-row justify-between')}>
					{isCompactToggle && (
						<SwitchPrimitives.Root
							className={cn(
								'focus-visible:ring-ring focus-visible:ring-offset-background peer inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-success data-[state=unchecked]:bg-slate-300',
								className
							)}
							{...props}
							ref={ref}
							id={labelId}
							data-aid={`toggle-${label.toLowerCase().replace(/\s/g, '_')}`}
							checked={checked}
						>
							<SwitchPrimitives.Thumb
								className={cn(
									'block h-4 w-4 rounded-full bg-white ring-0 transition-transform',
									isRtl
										? 'data-[state=checked]:-translate-x-4 data-[state=unchecked]:translate-x-0'
										: 'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0'
								)}
								dir={props.dir}
							/>
						</SwitchPrimitives.Root>
					)}

					<div className={cn('flex min-w-0 flex-col', isCompactToggle ? 'flex-row items-center gap-x-2' : 'flex-col')}>
						{titleAdornment ? (
							<div className="flex items-center gap-x-1">
								{labelElement}
								{titleAdornment}
							</div>
						) : (
							labelElement
						)}
						{isCompactToggle && description && <span className="text-sm text-slate-500">{description}</span>}
						{info && <InfoTooltip content={info} />}
					</div>

					{!isCompactToggle && (
						<SwitchPrimitives.Root
							className={cn(
								'focus-visible:ring-ring focus-visible:ring-offset-background peer inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-success data-[state=unchecked]:bg-slate-300',
								className
							)}
							{...props}
							ref={ref}
							id={labelId}
							data-aid={`toggle-${label.toLowerCase().replace(/\s/g, '_')}`}
							checked={checked}
						>
							<SwitchPrimitives.Thumb
								className={cn(
									'block h-4 w-4 rounded-full bg-white ring-0 transition-transform',
									isRtl
										? 'data-[state=checked]:-translate-x-4 data-[state=unchecked]:translate-x-0'
										: 'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0'
								)}
								dir={props.dir}
							/>
						</SwitchPrimitives.Root>
					)}
				</div>
				{children && checked && <div data-testid="toggle-children">{children}</div>}
			</div>
		);
	}
);
Toggle.displayName = SwitchPrimitives.Root.displayName;

export { Toggle };
